import { useCallback, useEffect, useRef, useState } from "react";
import CollaboratorsAPI from "../../../services/api/collaborators.api";
import { isConnectionError } from "../../../utils/axios";
import { reportClientError } from "../../../utils/error-reporter";
import { checkConnection } from "../../../utils/connection-check";
import logo from "../../../assets/gm-logo-name.svg";
import emailIcon from "../../../assets/email_icon.png";
import termIcon01 from "../../../assets/term_icon_01.png";
import termIcon02 from "../../../assets/term_icon_02.png";
import termIcon03 from "../../../assets/term_icon_03.png";
import SortableList from "../../components/sortable-list/sortable-list";
import { getUniqueRandomNumber } from "../../../utils/randomNumbers";
import {
  isSpecificCompetencyMode,
  selectCompetencySections,
  selectSpecificCompetencySections,
} from "./select-competency-sections";
import {
  clearLocalProgress,
  maxRev,
  pickLatestProgress,
  readLocalProgress,
  writeLocalProgress,
} from "./local-progress";

const STEPS = {
  TERMS: 'TERMS',
  DISC_PHASE: 'DISC_PHASE',
  // Aviso antes de la parte con tiempo: las preguntas ya están elegidas, pero el
  // tiempo no corre hasta que la persona confirma que está lista.
  PRE_COMPETENCY: 'PRE_COMPETENCY',
  COMPETENCY_PHASE: 'COMPETENCY_PHASE',
  INTRO_ANSWER_FUNDAMENTALS: 'INTRO_ANSWER_FUNDAMENTALS',
  ANSWER_FUNDAMENTALS: 'ANSWER_FUNDAMENTALS',
  FINISHED: 'FINISHED',
};

const MAX_ANSWER_FUNDAMENTALS = 2;
const MIN_FUNDAMENTAL_LENGTH = 150;

// Tiempo sugerido por pregunta (valor de un timer recién arrancado). La autoridad
// del tiempo restante ante recarga es el servidor, que devuelve remainingSeconds.
const QUESTION_TIME = parseInt(process.env.REACT_APP_QUESTION_TIMEOUT_SECONDS, 10) || 180;
const QUESTION_MINUTES = Math.round(QUESTION_TIME / 60);

// Lo que se le anuncia a la persona antes de empezar. La segunda parte son 8
// competencias de 3 preguntas, tanto por patrón DISC como en modo específicas.
const EXPECTED_COMPETENCY_QUESTIONS = 24;
// Tiempos medidos en producción (2026-10-21, 194 tests unificados terminados, de
// aceptar los términos a enviar): mediana 44 min y la mitad central entre 33 y 60.
// Por parte (medianas): autopercepción 10, situaciones 23 y cierre 9 min.
const TEST_DURATION_TEXT = '30 a 60 minutos';
const TEST_DURATION_TYPICAL = 'Tiempo promedio de respuesta: 45 minutos.';

// Mensajes de ánimo en la segunda parte, por número de pregunta (1-based).
const COMPETENCY_CHEERS = {
  7: 'Buen ritmo, sigue así',
  13: 'Ya vas en la mitad de esta parte',
  19: 'Ya casi: quedan 6 situaciones',
};

// Reintento de un guardado que no llegó al servidor.
const SAVE_RETRY_MS = 10000;
// Pausa tras el último cambio antes de guardar en el servidor lo que se está
// respondiendo dentro de un mismo paso (texto de una respuesta abierta, orden del DISC).
const PARTIAL_SAVE_DEBOUNCE_MS = 3000;
// Pausa antes de copiar al navegador los cambios que no pasan por persistProgress.
const LOCAL_SAVE_DEBOUNCE_MS = 500;
// Reintento automático del envío final mientras no llegue.
const SUBMIT_RETRY_MS = 15000;

// Errores en los que vale la pena reintentar: sin respuesta o falla del servidor.
// Un 4xx (p. ej. test inexistente) se repetiría igual.
const isRetryableError = (error) => isConnectionError(error) || error?.response?.status >= 500;

const isAutoFillEnabled = () => {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }
  try {
    return localStorage.getItem('discTestEasterEgg') === '35182a5354dfadb12e4c98f86d0479f6';
  } catch (error) {
    return false;
  }
};

const AUTOFILL_ENABLED = isAutoFillEnabled();

const UnifiedTest = (props) => {
  // `preview`: vista previa del panel (superadmins, ver modules/private/test-preview).
  // No guarda nada en el servidor ni en el navegador, no reporta errores y deja
  // avanzar sin responder. La página del candidato nunca lo pasa.
  const { test, preview = false, onPreviewRestart, onPreviewExit } = props;
  const [previewFast, setPreviewFast] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.TERMS);
  // Para callbacks asíncronos que necesitan el paso actual y no el de su render.
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const [loading, setLoading] = useState(false);

  // ── DISC phase state ─────────────────────────────────────────────────────
  const discSection = test.sections?.[0]; // "Su Estilo de Trabajo"
  const [discQuestions, setDiscQuestions] = useState([]);
  const [discFormData, setDiscFormData] = useState({ '0': {} });

  // ── Competency phase state ────────────────────────────────────────────────
  const [competencyQuestions, setCompetencyQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [startTestDate, setStartTestDate] = useState(null);
  const [finishTestDate, setFinishTestDate] = useState(null);
  const [answerFundamentalsIndexs, setAnswerFundamentalsIndexs] = useState([]);
  const [currentAnswerFundamentalsIndex, setCurrentAnswerFundamentalsIndex] = useState(0);
  const [isFinalStep, setIsFinalStep] = useState(false);
  // Semilla inicial del timer de pregunta (segundos). Al hidratar viene del servidor;
  // en flujo normal se arranca con el valor completo del límite.
  const [questionInitialSeconds, setQuestionInitialSeconds] = useState(null);
  // Inicio del test (al aceptar los términos), para mostrar el tiempo transcurrido.
  // Viaja en el snapshot de progreso; los tests empezados antes de este cambio no
  // lo traen y simplemente no muestran el reloj.
  const [testStartedAt, setTestStartedAt] = useState(null);
  const [testFinishedAt, setTestFinishedAt] = useState(null);
  // Confirmación del aviso de la segunda parte (no se persiste: si recarga, confirma de nuevo).
  const [readyForTimedPart, setReadyForTimedPart] = useState(false);

  useEffect(() => {
    if (!test?.sections) return;

    // Sesión persistida (issue #10): rehidratar sin re-aleatorizar. Se toma la copia
    // más nueva entre la del servidor y la de este navegador: si la red cortó los
    // guardados, la del navegador va más adelantada.
    const localProgress = preview ? null : readLocalProgress(test.collaboratorTestID);
    const { progress, fromLocal } = pickLatestProgress(test.progress, localProgress);
    revRef.current = maxRev(test.progress, localProgress);
    if (progress) {
      hydrateFromProgress(progress, fromLocal);
      if (fromLocal) syncLocalProgressToServer(progress);
      return;
    }

    // Section 0: DISC drag-and-drop
    if (discSection) {
      const qs = discSection.questions.map((question, index) => ({
        id: question.id,
        section: 0,
        options: question.options.map((opt) => ({
          id: opt.questionOptionID,
          text: `${opt.spanish.charAt().toUpperCase()}${opt.spanish.slice(1)}`,
        })),
        title: `${index + 1}. ${question.spanish}`,
        subtitle: 'Toque o arrastre las opciones para ordenarlas según su prioridad, de mayor a menor relevancia.',
      }));
      setDiscQuestions(qs);
    }

    // Sections 1–8: competency questions (flattened and shuffled)
    const competencySections = test.sections.slice(1);
    const allQuestions = [];
    competencySections.forEach((section) => {
      section.questions.forEach((question) => {
        allQuestions.push(question);
      });
    });
    for (let i = allQuestions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allQuestions[i], allQuestions[j]] = [allQuestions[j], allQuestions[i]];
    }
    setCompetencyQuestions(allQuestions);
  }, [test]);

  // ── Persistencia de sesión (issue #10) ──────────────────────────────────────
  const collaboratorTestID = test.collaboratorTestID;
  // Contador de snapshots (ver local-progress.js).
  const revRef = useRef(0);
  // Último snapshot que el servidor todavía no confirmó. Se envía de a uno: con
  // reintentos en paralelo, uno viejo podía llegar después y pisar a uno nuevo.
  const pendingSaveRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const retryTimerRef = useRef(null);
  const fundamentalSaveTimerRef = useRef(null);
  const [serverSaveFailing, setServerSaveFailing] = useState(false);

  // ── Prueba de conexión (ver connection-check.ts) ──────────────────────────
  // Corre en segundo plano mientras la persona lee los términos; al aceptarlos se
  // espera su resultado, y si la red bloquea la plataforma se avisa antes de empezar.
  const connectionCheckRef = useRef(null);
  const [verifyingConnection, setVerifyingConnection] = useState(false);
  const [connectionBlocked, setConnectionBlocked] = useState(false);

  const runConnectionCheck = () => {
    connectionCheckRef.current = checkConnection().then((status) => {
      if (status === 'blocked') reportClientError('connection_blocked', 'La prueba de conexión no pasó por ninguna vía');
      if (status === 'alternate') reportClientError('connection_alternate', 'La prueba de conexión solo pasó por la vía alternativa', {}, 'info');
      return status;
    });
    return connectionCheckRef.current;
  };

  // ── Envío final que no se rinde ────────────────────────────────────────────
  // Si el envío no llega, se reintenta solo en vez de dejar un aviso que se puede
  // ignorar y cerrar la página creyendo que se envió.
  const [submitRetrying, setSubmitRetrying] = useState(false);
  const submitRetryTimerRef = useRef(null);
  const submitFormRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(retryTimerRef.current);
    clearTimeout(fundamentalSaveTimerRef.current);
    clearTimeout(submitRetryTimerRef.current);
  }, []);

  useEffect(() => {
    if (preview) return;
    runConnectionCheck().then((status) => {
      // Test retomado (ya pasó los términos): no se frena, pero se avisa desde ya.
      if (status === 'blocked' && currentStepRef.current !== STEPS.TERMS) setServerSaveFailing(true);
    });
  }, []);

  // Cerrar o recargar la página con respuestas que aún no llegan al servidor: el
  // navegador pide confirmación. Lo guardado en este equipo no se pierde, pero si
  // la persona no vuelve a abrir el link en este mismo navegador, nunca se envía.
  useEffect(() => {
    if (!serverSaveFailing && !submitRetrying) return;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [serverSaveFailing, submitRetrying]);

  const hydrateFromProgress = (progress, fromLocal) => {
    if (progress.discQuestions) setDiscQuestions(progress.discQuestions);
    if (progress.discFormData) setDiscFormData(progress.discFormData);
    // `?.length` y no truthiness: un snapshot con `[]` es truthy y restauraría la fase
    // de competencias sin preguntas, dejando la sesión sin salida en cada recarga.
    if (progress.competencyQuestions?.length) setCompetencyQuestions(progress.competencyQuestions);
    if (progress.startTestDate) setStartTestDate(progress.startTestDate);
    if (progress.finishTestDate) setFinishTestDate(progress.finishTestDate);
    if (progress.answerFundamentalsIndexs) setAnswerFundamentalsIndexs(progress.answerFundamentalsIndexs);
    if (Number.isInteger(progress.currentAnswerFundamentalsIndex)) setCurrentAnswerFundamentalsIndex(progress.currentAnswerFundamentalsIndex);
    if (Number.isInteger(progress.currentQuestionIndex)) setCurrentQuestionIndex(progress.currentQuestionIndex);
    if (progress.isFinalStep) setIsFinalStep(progress.isFinalStep);
    if (progress.testStartedAt) setTestStartedAt(progress.testStartedAt);
    // Semilla de timers desde el servidor (fuente de verdad del tiempo restante).
    // El backend devuelve estos campos en el nivel superior de la respuesta (hermanos
    // de `progress`), no dentro de `progress`. Si se retoma desde la copia local en
    // otra pregunta, el tiempo que trae el servidor es de una pregunta anterior.
    const questionSeconds = fromLocal && progress.currentQuestionIndex !== test.progress?.currentQuestionIndex
      ? null
      : test.questionRemainingSeconds;
    setQuestionInitialSeconds(questionSeconds != null ? questionSeconds : QUESTION_TIME);
    // El paso se fija al final para que el resto del estado ya esté disponible.
    if (progress.currentStep) setCurrentStep(progress.currentStep);
  };

  // Construye el snapshot de progreso a partir del estado actual (con overrides para
  // los valores que se están cambiando en la misma transición, dado que setState es async).
  const buildProgress = (overrides = {}) => ({
    currentStep,
    currentQuestionIndex,
    discQuestions,
    discFormData,
    competencyQuestions,
    startTestDate,
    finishTestDate,
    answerFundamentalsIndexs,
    currentAnswerFundamentalsIndex,
    isFinalStep,
    testStartedAt,
    ...overrides,
  });

  // Arma el snapshot con un rev nuevo y lo deja primero en este navegador, que no
  // depende de la red.
  const snapshotProgress = (overrides = {}) => {
    revRef.current += 1;
    const snapshot = { ...buildProgress(overrides), rev: revRef.current };
    if (!preview) writeLocalProgress(collaboratorTestID, snapshot);
    return snapshot;
  };

  // Solo usa refs y setters estables, así que también sirve desde un setTimeout.
  const flushProgress = () => {
    if (preview) {
      pendingSaveRef.current = null;
      return;
    }
    if (saveInFlightRef.current || !pendingSaveRef.current) return;
    const snapshot = pendingSaveRef.current;
    saveInFlightRef.current = true;
    CollaboratorsAPI.saveTestProgress(collaboratorTestID, snapshot)
      .then(() => {
        if (pendingSaveRef.current === snapshot) pendingSaveRef.current = null;
        setServerSaveFailing(false);
      })
      .catch((error) => {
        console.error('No se pudo guardar el progreso del test', error);
        if (!isRetryableError(error)) {
          if (pendingSaveRef.current === snapshot) pendingSaveRef.current = null;
          return;
        }
        // Queda en el navegador; se reintenta hasta que el servidor lo reciba.
        // El reporte no se repite en cada reintento (el reporter descarta duplicados).
        reportClientError('progress_save_failed', error);
        setServerSaveFailing(true);
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(flushProgress, SAVE_RETRY_MS);
      })
      .finally(() => {
        saveInFlightRef.current = false;
        // Llegó un snapshot más nuevo mientras éste viajaba.
        if (pendingSaveRef.current && pendingSaveRef.current !== snapshot) flushProgress();
      });
  };

  const persistProgress = (overrides = {}) => {
    pendingSaveRef.current = snapshotProgress(overrides);
    flushProgress();
  };

  // Se retomó desde la copia local: el servidor quedó atrás y hay que ponerlo al día.
  // Si nunca supo que el test empezó (falló el inicio de sesión), se inicia primero.
  const syncLocalProgressToServer = (progress) => {
    const start = test.progress
      ? Promise.resolve()
      : CollaboratorsAPI.startTestSession(collaboratorTestID, progress)
        .catch((error) => console.error('No se pudo iniciar la sesión del test', error));
    start.finally(() => {
      pendingSaveRef.current = progress;
      flushProgress();
    });
  };

  // Copia al navegador lo que no pasa por persistProgress: el orden del DISC
  // mientras se arrastra y el texto de las respuestas abiertas mientras se escribe.
  useEffect(() => {
    if (currentStep === STEPS.TERMS || currentStep === STEPS.FINISHED) return;
    const id = setTimeout(() => snapshotProgress(), LOCAL_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [discFormData, competencyQuestions, currentStep]);

  // La primera parte (18 preguntas DISC) también va al servidor mientras se ordena,
  // no solo al terminarla: así se puede retomar aunque se cambie de equipo.
  // currentStep en las dependencias cancela el guardado pendiente al avanzar, que si
  // no devolvería el progreso del servidor a esta fase.
  useEffect(() => {
    if (currentStep !== STEPS.DISC_PHASE) return;
    const id = setTimeout(() => persistProgress(), PARTIAL_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [discFormData, currentStep]);

  // ── DISC helpers ─────────────────────────────────────────────────────────
  const handleDiscQuestionChange = useCallback((questionId, sortedItems) => {
    setDiscFormData(prev => ({
      ...prev,
      '0': { ...prev['0'], [questionId]: sortedItems },
    }));
  }, []);

  const validateDiscForm = () => {
    const missingQuestions = [];
    discQuestions.forEach(question => {
      const questionData = discFormData['0'][question.id];
      if (!questionData || questionData.some(item => item === null)) {
        missingQuestions.push(question.title);
      }
    });
    return { isValid: missingQuestions.length === 0, missingQuestions };
  };

  // ── Competency helpers ────────────────────────────────────────────────────
  const isOptionSelected = () => {
    const question = competencyQuestions[currentQuestionIndex];
    return question?.options.some(option => option.selected);
  };

  const onChangeOption = (indexOption) => () => {
    // Copia inmutable de la pregunta y del array (no mutar el estado previo ni el snapshot).
    const updated = competencyQuestions.map((question, questionIndex) => {
      if (questionIndex !== currentQuestionIndex) return question;
      return {
        ...question,
        options: question.options.map((option, index) => {
          const { id, questionOptionID, spanish, levelOption } = option;
          const localOption = { id, questionOptionID, spanish, levelOption };
          if (index === indexOption) localOption.selected = true;
          return localOption;
        }),
      };
    });
    setCompetencyQuestions(updated);
    persistProgress({ competencyQuestions: updated });
  };

  const onChangeFundamentalInput = (e) => {
    if (e.target.value.length <= 700) {
      const targetIndex = answerFundamentalsIndexs[currentAnswerFundamentalsIndex];
      // Copia inmutable de la pregunta objetivo (no mutar el estado previo).
      const updated = competencyQuestions.map((question, questionIndex) =>
        questionIndex === targetIndex ? { ...question, answerFundamental: e.target.value } : question
      );
      setCompetencyQuestions(updated);
      // El texto también va al servidor, pero solo cuando se deja de escribir.
      clearTimeout(fundamentalSaveTimerRef.current);
      fundamentalSaveTimerRef.current = setTimeout(
        () => persistProgress({ competencyQuestions: updated }),
        PARTIAL_SAVE_DEBOUNCE_MS
      );
    }
  };

  // ── Shared helpers ─────────────────────────────────────────────────────────
  const buildDiscAnswers = () => {
    const discAnswers = [];
    Object.keys(discFormData['0'] || {}).forEach((questionID) => {
      const options = discFormData['0'][questionID];
      options.forEach((opt, idx) => {
        discAnswers.push({
          questionOptionID: opt.id,
          optionPriority: options.length - idx,
        });
      });
    });
    return discAnswers;
  };

  // Orden al azar de las 18 preguntas DISC, para calcular las competencias de la
  // segunda parte cuando en la vista previa se avanza sin ordenarlas.
  const randomDiscAnswers = () => {
    const answers = [];
    discQuestions.forEach((question) => {
      const shuffled = [...question.options];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      shuffled.forEach((opt, idx) => answers.push({ questionOptionID: opt.id, optionPriority: shuffled.length - idx }));
    });
    return answers;
  };

  // Las preguntas de la segunda parte: las 8 competencias (según el par DISC o las
  // elegidas al enviar el test), mezcladas.
  const prepareCompetencyQuestions = async (discAnswers) => {
    // Modo "competencias específicas" (#67): las 8 se eligieron al enviar el test,
    // así que no hay que preguntarle al servidor por el par de colores predominante.
    // `discColorPair` sólo calcula qué secciones servir y no persiste nada, de modo
    // que saltearlo no altera el resultado DISC: éste se recalcula al evaluar, a
    // partir de las respuestas ya guardadas.
    const specificCompetences = test?.selectedCompetences;
    const isSpecificMode = isSpecificCompetencyMode(specificCompetences);
    const competencySections = test.sections.slice(1);

    const relevantSections = isSpecificMode
      ? selectSpecificCompetencySections(competencySections, specificCompetences)
      : selectCompetencySections(
          competencySections,
          (await CollaboratorsAPI.discColorPair(discAnswers)).data
        );

    const filteredQuestions = [];
    relevantSections.forEach((section) => {
      section.questions.forEach((q) => filteredQuestions.push(q));
    });
    // Sin preguntas no hay fase de competencias que servir: es preferible fallar acá
    // —el catch deja al colaborador en la fase DISC, pudiendo reintentar— que avanzar
    // y persistir un snapshot vacío. Se valida sobre las preguntas y no sobre las
    // secciones para cubrir también secciones sin preguntas asociadas.
    if (filteredQuestions.length === 0) {
      throw new Error(
        isSpecificMode
          ? 'No se resolvieron secciones para las competencias seleccionadas'
          : 'No se resolvieron secciones de competencias para el par de colores DISC'
      );
    }
    for (let i = filteredQuestions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [filteredQuestions[i], filteredQuestions[j]] = [filteredQuestions[j], filteredQuestions[i]];
    }
    return filteredQuestions;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const submitForm = () => {
    if (preview) {
      // En la vista previa no se envía nada: se muestra el final.
      setTestFinishedAt(Date.now());
      setCurrentStep(STEPS.FINISHED);
      window.scrollTo(0, 0);
      return;
    }
    const answers = buildDiscAnswers();

    // Competency answers (sections 1–8)
    competencyQuestions.forEach((question) => {
      question.options.forEach((option) => {
        const answer = {
          questionOptionID: option.questionOptionID,
          optionPriority: option.selected ? 1 : 0,
        };
        if (option.selected) {
          if (question.answerFundamental) answer.answerFundamental = question.answerFundamental;
          answer.startQuestionDate = question.startQuestionDate;
          answer.finishQuestionDate = question.finishQuestionDate;
        }
        answers.push(answer);
      });
    });

    const body = {
      collaboratorTestID: test.collaboratorTestID,
      startTestDate,
      finishTestDate,
      answers,
    };

    const finishTest = () => {
      clearTimeout(retryTimerRef.current);
      clearTimeout(fundamentalSaveTimerRef.current);
      clearTimeout(submitRetryTimerRef.current);
      pendingSaveRef.current = null;
      clearLocalProgress(collaboratorTestID);
      setServerSaveFailing(false);
      setSubmitRetrying(false);
      setTestFinishedAt(Date.now());
      setCurrentStep(STEPS.FINISHED);
    };

    clearTimeout(submitRetryTimerRef.current);
    // Si el envío falla, que el servidor tenga al menos el texto de esta última respuesta.
    if (!submitRetrying) persistProgress({ competencyQuestions });
    setLoading(true);
    CollaboratorsAPI.postTestAnswers(body)
      .then(finishTest)
      .catch((error) => {
        // Un intento anterior sí llegó pero su respuesta se perdió en la red: el
        // back rechaza el duplicado, y el test ya quedó enviado.
        if (error?.response?.status === 422 && String(error.response.data || '').includes('ya fue respondido')) {
          finishTest();
          return;
        }
        console.error('No se pudieron enviar las respuestas del test', error);
        reportClientError('submit_failed', error);
        setLoading(false);
        if (isRetryableError(error)) {
          // Sin conexión o falla del servidor: se reintenta solo hasta que llegue.
          setSubmitRetrying(true);
          submitRetryTimerRef.current = setTimeout(() => submitFormRef.current(), SUBMIT_RETRY_MS);
          return;
        }
        // El back respondió que algo no está bien con el envío: reintentar daría lo mismo.
        alert(
          'No pudimos registrar tus respuestas. Tus respuestas quedaron guardadas en este equipo.\n\n' +
          'Por favor contacta a la persona que te envió la prueba y no cierres esta página.'
        );
      });
  };
  submitFormRef.current = submitForm;

  // ── Navigation ────────────────────────────────────────────────────────────
  const startTest = () => {
    const startedAt = testStartedAt || new Date().toISOString();
    setConnectionBlocked(false);
    setTestStartedAt(startedAt);
    setCurrentStep(STEPS.DISC_PHASE);
    // Inicia la sesión en el servidor (fija startedAt/opened, congela el snapshot DISC).
    if (!preview) {
      CollaboratorsAPI.startTestSession(collaboratorTestID, snapshotProgress({ currentStep: STEPS.DISC_PHASE, testStartedAt: startedAt }))
        .catch((error) => console.error('No se pudo iniciar la sesión del test', error));
    }
    window.scrollTo(0, 0);
  };

  const retryConnectionCheck = async () => {
    setVerifyingConnection(true);
    const status = await runConnectionCheck();
    setVerifyingConnection(false);
    if (status !== 'blocked') startTest();
  };

  const nextAction = async () => {
    // El guardado pendiente de una respuesta abierta se armó con el paso actual: si
    // se dispara después de avanzar, devolvería el progreso a este paso.
    clearTimeout(fundamentalSaveTimerRef.current);

    if (currentStep === STEPS.TERMS) {
      if (preview) {
        startTest();
        return;
      }
      // Antes de empezar se confirma que las respuestas van a poder llegar.
      setVerifyingConnection(true);
      const status = await (connectionCheckRef.current || runConnectionCheck());
      setVerifyingConnection(false);
      if (status === 'blocked') {
        setConnectionBlocked(true);
        window.scrollTo(0, 0);
        return;
      }
      startTest();
      return;
    }

    if (currentStep === STEPS.DISC_PHASE) {
      const validation = validateDiscForm();
      if (!validation.isValid && !preview) {
        alert('Por favor complete todas las preguntas antes de continuar.\n\n' +
          'Preguntas incompletas:\n' +
          validation.missingQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n'));
        return;
      }

      const discAnswers = validation.isValid ? buildDiscAnswers() : randomDiscAnswers();

      setLoading(true);
      try {
        const filteredQuestions = await prepareCompetencyQuestions(discAnswers);
        // Se congela el set de preguntas pero el tiempo todavía no corre: primero se
        // muestra el aviso de la parte con tiempo (PRE_COMPETENCY). El servidor solo
        // sella tiempos al recibir COMPETENCY_PHASE.
        setCompetencyQuestions(filteredQuestions);
        setCurrentQuestionIndex(0);
        setReadyForTimedPart(false);
        setCurrentStep(STEPS.PRE_COMPETENCY);
        persistProgress({
          competencyQuestions: filteredQuestions,
          currentQuestionIndex: 0,
          currentStep: STEPS.PRE_COMPETENCY,
        });
        window.scrollTo(0, 0);
      } catch (error) {
        if (!preview) reportClientError('competency_prepare_failed', error);
        // Genérico: en modo específicas no se llama al endpoint DISC, así que hablar
        // del "test DISC" mandaría a mirar el lugar equivocado.
        alert(
          'No pudimos preparar la segunda parte del test. Tus respuestas de la primera parte quedaron guardadas.\n\n' +
          'Revisa tu conexión y vuelve a presionar "Continuar".'
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (currentStep === STEPS.PRE_COMPETENCY) {
      if (!readyForTimedPart) return;
      // Recién acá arranca el tiempo de la segunda parte (antes ocurría al salir del DISC).
      const updatedQuestions = [...competencyQuestions];
      if (updatedQuestions[0]) updatedQuestions[0] = { ...updatedQuestions[0], startQuestionDate: new Date() };
      const startDate = new Date();
      setCompetencyQuestions(updatedQuestions);
      setStartTestDate(startDate);
      setCurrentQuestionIndex(0);
      setQuestionInitialSeconds(QUESTION_TIME);
      setCurrentStep(STEPS.COMPETENCY_PHASE);
      // Persiste el snapshot congelado de competencias; el servidor sella competencyStartedAt.
      persistProgress({
        competencyQuestions: updatedQuestions,
        startTestDate: startDate,
        currentQuestionIndex: 0,
        currentStep: STEPS.COMPETENCY_PHASE,
      });
      window.scrollTo(0, 0);
      return;
    }

    if (currentStep === STEPS.COMPETENCY_PHASE) {
      if (currentQuestionIndex < competencyQuestions.length - 1) {
        if (isOptionSelected() || preview) {
          const updatedQuestions = [...competencyQuestions];
          updatedQuestions[currentQuestionIndex].finishQuestionDate = new Date();
          updatedQuestions[currentQuestionIndex + 1].startQuestionDate = new Date();
          setCompetencyQuestions(updatedQuestions);
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          persistProgress({
            competencyQuestions: updatedQuestions,
            currentQuestionIndex: currentQuestionIndex + 1,
            currentStep: STEPS.COMPETENCY_PHASE,
          });
          window.scrollTo(0, 0);
        } else {
          alert('Por favor, selecciona una opción');
        }
      } else {
        if (isOptionSelected() || preview) {
        const updatedQuestions = [...competencyQuestions];
        updatedQuestions[currentQuestionIndex].finishQuestionDate = new Date();
        setCompetencyQuestions(updatedQuestions);
        setCurrentStep(STEPS.INTRO_ANSWER_FUNDAMENTALS);
        persistProgress({
          competencyQuestions: updatedQuestions,
          currentStep: STEPS.INTRO_ANSWER_FUNDAMENTALS,
        });
        window.scrollTo(0, 0);
        } else {
          alert('Por favor, selecciona una opción');
          return;
        }

      }
      return;
    }

    if (currentStep === STEPS.INTRO_ANSWER_FUNDAMENTALS) {
      const questionsHighAndVeryHigh = competencyQuestions.filter((question, index) => {
        const isHighOrVeryHigh = question.options.find(
          opt => (opt.levelOption === 'Muy alto' || opt.levelOption === 'Alto') && opt.selected
        );
        if (isHighOrVeryHigh) { question.originalIndex = index; return true; }
        return false;
      });
      const questionTarget = questionsHighAndVeryHigh.length >= 2 ? questionsHighAndVeryHigh : competencyQuestions;
      let questionOneIndex = getUniqueRandomNumber(questionTarget.length);
      let questionTwoIndex = getUniqueRandomNumber(questionTarget.length, [questionOneIndex]);
      if (questionOneIndex === -1 || questionTwoIndex === -1) {
        questionOneIndex = 0;
        questionTwoIndex = questionTarget.length > 1 ? 1 : 0;
      }
      questionOneIndex = questionTarget[questionOneIndex].originalIndex ?? questionOneIndex;
      questionTwoIndex = questionTarget[questionTwoIndex].originalIndex ?? questionTwoIndex;

      const updatedQuestions = [...competencyQuestions];
      updatedQuestions[questionOneIndex].answerFundamental = '';
      updatedQuestions[questionTwoIndex].answerFundamental = '';
      const finishDate = new Date();
      setCompetencyQuestions(updatedQuestions);
      setAnswerFundamentalsIndexs([questionOneIndex, questionTwoIndex]);
      setFinishTestDate(finishDate);
      setCurrentStep(STEPS.ANSWER_FUNDAMENTALS);
      persistProgress({
        competencyQuestions: updatedQuestions,
        answerFundamentalsIndexs: [questionOneIndex, questionTwoIndex],
        finishTestDate: finishDate,
        currentStep: STEPS.ANSWER_FUNDAMENTALS,
      });
      window.scrollTo(0, 0);
      return;
    }

    if (currentStep === STEPS.ANSWER_FUNDAMENTALS) {
      const currentFundamental = competencyQuestions[answerFundamentalsIndexs[currentAnswerFundamentalsIndex]].answerFundamental;
      if (!preview && (!currentFundamental || currentFundamental.length < 1)) {
        alert('Por favor, fundamenta tu respuesta');
        return;
      }
      if (!preview && currentFundamental.length < MIN_FUNDAMENTAL_LENGTH) {
        alert(`Por favor, fundamenta tu respuesta con al menos ${MIN_FUNDAMENTAL_LENGTH} caracteres`);
        return;
      }
      if (currentAnswerFundamentalsIndex + 1 === MAX_ANSWER_FUNDAMENTALS) {
        submitForm();
        return;
      }
      setIsFinalStep(true);
      setCurrentAnswerFundamentalsIndex(currentAnswerFundamentalsIndex + 1);
      persistProgress({
        competencyQuestions,
        isFinalStep: true,
        currentAnswerFundamentalsIndex: currentAnswerFundamentalsIndex + 1,
        currentStep: STEPS.ANSWER_FUNDAMENTALS,
      });
      window.scrollTo(0, 0);
      return;
    }
  };

  // ── Vista previa: saltar a cualquier pantalla ──────────────────────────────
  // Arma lo mínimo que cada pantalla necesita (preguntas de la parte 2, respuestas
  // a fundamentar) sin pasar por las anteriores. Nada de esto se guarda.
  const previewGoTo = async (step, questionIndex = 0) => {
    clearTimeout(fundamentalSaveTimerRef.current);
    if (step === STEPS.TERMS) {
      onPreviewRestart?.();
      return;
    }
    setTestStartedAt((current) => current || new Date().toISOString());
    setTestFinishedAt(null);
    if (step === STEPS.DISC_PHASE) {
      setCurrentStep(step);
      window.scrollTo(0, 0);
      return;
    }

    let questions = competencyQuestions;
    if ([STEPS.TERMS, STEPS.DISC_PHASE].includes(currentStep)) {
      setLoading(true);
      try {
        questions = await prepareCompetencyQuestions(validateDiscForm().isValid ? buildDiscAnswers() : randomDiscAnswers());
      } catch (error) {
        alert('No se pudo preparar la segunda parte del test. Revisa tu conexión e inténtalo de nuevo.');
        return;
      } finally {
        setLoading(false);
      }
    }

    if (step === STEPS.ANSWER_FUNDAMENTALS) {
      const chosen = questions
        .map((question, index) => ({ index, high: question.options.some((opt) => opt.selected && ['Muy alto', 'Alto'].includes(opt.levelOption)) }))
        .filter((item, _i, all) => item.high || all.filter((x) => x.high).length < 2)
        .map((item) => item.index)
        .sort(() => Math.random() - 0.5)
        .slice(0, MAX_ANSWER_FUNDAMENTALS);
      questions = questions.map((question, index) =>
        chosen.includes(index) && question.answerFundamental === undefined ? { ...question, answerFundamental: '' } : question
      );
      setAnswerFundamentalsIndexs(chosen);
      setCurrentAnswerFundamentalsIndex(0);
      setIsFinalStep(false);
    }

    setCompetencyQuestions(questions);
    if (step === STEPS.PRE_COMPETENCY) setReadyForTimedPart(false);
    if (step === STEPS.COMPETENCY_PHASE) {
      setStartTestDate((current) => current || new Date());
      setQuestionInitialSeconds(QUESTION_TIME);
      setCurrentQuestionIndex(Math.min(questionIndex, questions.length - 1));
    }
    if (step === STEPS.FINISHED) setTestFinishedAt(Date.now());
    setCurrentStep(step);
    window.scrollTo(0, 0);
  };

  const autoFill = () => {
    if (!AUTOFILL_ENABLED && !preview) return;

    // Fill DISC: put all options in shuffled order for every question
    const newDiscFormData = { '0': {} };
    discQuestions.forEach((question) => {
      const shuffled = question.options.map((opt) => ({ ...opt }));
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      newDiscFormData['0'][question.id] = shuffled;
    });
    setDiscFormData(newDiscFormData);

    // Fill competency: select a random option for every question
    const filledQuestions = competencyQuestions.map((question) => {
      const randomIndex = Math.floor(Math.random() * question.options.length);
      return {
        ...question,
        options: question.options.map((opt, index) => ({ ...opt, selected: index === randomIndex })),
      };
    });
    setCompetencyQuestions(filledQuestions);
  };

  const getButtonText = () => {
    if (verifyingConnection) return 'Verificando tu conexión...';
    if (loading) return 'Enviando...';
    if (submitRetrying) return 'Reintentar ahora';
    if (currentStep === STEPS.TERMS) return 'Aceptar y Comenzar';
    if (currentStep === STEPS.DISC_PHASE) return 'Continuar a la segunda parte';
    if (currentStep === STEPS.PRE_COMPETENCY) return 'Comenzar la segunda parte';
    if (isFinalStep) return 'Enviar Respuestas';
    return 'Continuar';
  };

  const discDone = discQuestions.filter((question) => {
    const sorted = discFormData['0'][question.id];
    return sorted && sorted.length === question.options.length && sorted.every(Boolean);
  }).length;
  const competencyChosen = [STEPS.PRE_COMPETENCY, STEPS.COMPETENCY_PHASE, STEPS.INTRO_ANSWER_FUNDAMENTALS, STEPS.ANSWER_FUNDAMENTALS, STEPS.FINISHED].includes(currentStep);
  const competencyTotal = competencyChosen && competencyQuestions.length ? competencyQuestions.length : EXPECTED_COMPETENCY_QUESTIONS;

  return (
    <>
      <div className="gm-test-container gm-unified">
        <div className="test-header">
          <div className="test-header-inner">
            <div className="test-header-row">
              <img src={logo} alt="growme-logo" />
              {currentStep !== STEPS.TERMS && (
                <ElapsedTime startedAt={testStartedAt} stoppedAt={testFinishedAt} />
              )}
            </div>
            <TestProgress
              step={currentStep}
              discDone={discDone}
              discTotal={discQuestions.length || 18}
              competencyIndex={currentQuestionIndex}
              competencyTotal={competencyTotal}
              fundamentalIndex={currentAnswerFundamentalsIndex}
            />
          </div>
        </div>

        {serverSaveFailing && currentStep !== STEPS.FINISHED && (
          <div className="test-save-warning" role="status">
            Estamos teniendo problemas para conectarnos. Tus respuestas se están guardando
            en este equipo y se enviarán apenas vuelva la conexión: puedes seguir respondiendo,
            pero no cambies de navegador ni de computador.
          </div>
        )}

        <div className="test-main">
          <div className="test-card">
            <div className="test-card-header">
              {currentStep === STEPS.COMPETENCY_PHASE && (
                <>
                  <p>Pregunta {currentQuestionIndex + 1} de {competencyQuestions.length}</p>
                  {(COMPETENCY_CHEERS[currentQuestionIndex + 1] || (currentQuestionIndex === competencyQuestions.length - 1 && 'Última situación')) && (
                    <p className="test-cheer-pill-row">
                      <span className="test-cheer-pill">
                        {COMPETENCY_CHEERS[currentQuestionIndex + 1] || 'Última situación'}
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="test-card-body">
              {currentStep === STEPS.TERMS && connectionBlocked && (
                <ConnectionBlocked
                  verifying={verifyingConnection}
                  onRetry={retryConnectionCheck}
                  onContinue={startTest}
                />
              )}
              {currentStep === STEPS.TERMS && !connectionBlocked && <TestTerms />}

              {currentStep === STEPS.DISC_PHASE && (
                <div className="test-section">
                  <h2>PRIMERA PARTE: AUTOPERCEPCIÓN</h2>
                  <h3>{discSection?.title}</h3>
                  <div className="test-part-chip">
                    <span className="test-chip">Esta parte no tiene tiempo límite</span>
                  </div>
                  <p className="intructions-p">Instrucciones</p>
                  <p className="description-p">{discSection?.description}</p>
                  {AUTOFILL_ENABLED && (
                    <button
                      type="button"
                      style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', marginBottom: '16px' }}
                      onClick={autoFill}
                    >
                      Fill All (Test)
                    </button>
                  )}
                  {discQuestions.map((question) => {
                    const sorted = discFormData['0'][question.id];
                    const complete = !!sorted && sorted.length === question.options.length && sorted.every(Boolean);
                    return (
                      <div key={`disc-${question.id}`} className={`disc-question${complete ? ' complete' : ''}`}>
                        <SortableList
                          questionId={question.id}
                          initialAvailableItems={question.options}
                          initialDroppedItems={discFormData['0'][question.id]}
                          onSortChange={handleDiscQuestionChange}
                          numberOfDropZones={question.options.length}
                          title={question.title}
                          subtitle={question.subtitle}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {currentStep === STEPS.PRE_COMPETENCY && (
                <PreCompetency
                  questionCount={competencyQuestions.length}
                  ready={readyForTimedPart}
                  onReadyChange={setReadyForTimedPart}
                />
              )}

              {currentStep === STEPS.COMPETENCY_PHASE && (
                <>
                  {AUTOFILL_ENABLED && (
                    <button
                      type="button"
                      style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', marginBottom: '8px' }}
                      onClick={autoFill}
                    >
                      Fill All (Test)
                    </button>
                  )}
                  <Question
                    question={competencyQuestions[currentQuestionIndex]}
                  onChangeOption={onChangeOption}
                    resetTimer={currentQuestionIndex}
                    initialSeconds={questionInitialSeconds}
                    tickMs={preview && previewFast ? 50 : 1000}
                  />
                </>
              )}

              {currentStep === STEPS.INTRO_ANSWER_FUNDAMENTALS && (
                <IntroAnswerFundamentals questionCount={competencyQuestions.length} />
              )}

              {currentStep === STEPS.ANSWER_FUNDAMENTALS && (
                <AnswerFundamentals
                  question={competencyQuestions[answerFundamentalsIndexs[currentAnswerFundamentalsIndex]]}
                  onChangeFundamentalInput={onChangeFundamentalInput}
                />
              )}

              {currentStep === STEPS.FINISHED && <Finished />}

              {submitRetrying && currentStep !== STEPS.FINISHED && (
                <div className="test-submit-pending" role="alert">
                  <strong>Tus respuestas aún no se han enviado.</strong>
                  <p>
                    No pudimos conectarnos con la plataforma. Tus respuestas están guardadas en este
                    equipo y estamos reintentando automáticamente cada 15 segundos.
                    <strong> No cierres esta página</strong> hasta que veas el mensaje de test finalizado.
                  </p>
                  <p>
                    Si después de unos minutos sigue sin enviarse, conéctate a otra red (por ejemplo,
                    los datos móviles de tu celular) sin cerrar esta página.
                  </p>
                </div>
              )}
            </div>

            <div className="test-card-footer">
              {currentStep !== STEPS.FINISHED && !connectionBlocked && (
                <button
                  onClick={nextAction}
                  disabled={loading || verifyingConnection || (currentStep === STEPS.PRE_COMPETENCY && !readyForTimedPart)}
                >
                  {getButtonText()}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {preview && (
        <PreviewPanel
          step={currentStep}
          busy={loading}
          competencyIndex={currentQuestionIndex}
          competencyTotal={competencyTotal}
          fast={previewFast}
          onFastChange={setPreviewFast}
          onGo={previewGoTo}
          onFill={autoFill}
          onExit={onPreviewExit}
        />
      )}
    </>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const PREVIEW_STEPS = [
  { step: STEPS.TERMS, label: 'Bienvenida' },
  { step: STEPS.DISC_PHASE, label: 'Parte 1' },
  { step: STEPS.PRE_COMPETENCY, label: 'Aviso parte 2' },
  { step: STEPS.COMPETENCY_PHASE, label: 'Parte 2' },
  { step: STEPS.INTRO_ANSWER_FUNDAMENTALS, label: 'Intro cierre' },
  { step: STEPS.ANSWER_FUNDAMENTALS, label: 'Cierre' },
  { step: STEPS.FINISHED, label: 'Final' },
];

// Panel flotante de la vista previa (no aparece en el test del candidato).
const PreviewPanel = ({ step, busy, competencyIndex, competencyTotal, fast, onFastChange, onGo, onFill, onExit }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="test-preview-panel-wrap">
      {open && (
        <aside className="test-preview-panel" aria-label="Controles de la vista previa">
          <p className="test-preview-panel-hint">
            <strong>Vista previa.</strong> Es el test real que ve un candidato, pero no guarda nada y puedes avanzar sin responder.
          </p>
          <span className="test-preview-panel-label">Ir a</span>
          <div className="test-preview-panel-steps">
            {PREVIEW_STEPS.map((item) => (
              <button
                key={item.step}
                type="button"
                disabled={busy}
                aria-current={item.step === step ? 'step' : undefined}
                onClick={() => onGo(item.step)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <label className="test-preview-panel-row" htmlFor="preview-question">
            <span>Pregunta de la parte 2</span>
            <select
              id="preview-question"
              value={step === STEPS.COMPETENCY_PHASE ? competencyIndex : ''}
              disabled={busy}
              onChange={(e) => onGo(STEPS.COMPETENCY_PHASE, Number(e.target.value))}
            >
              <option value="" disabled>—</option>
              {Array.from({ length: competencyTotal }, (_, i) => (
                <option key={i} value={i}>{i + 1} de {competencyTotal}</option>
              ))}
            </select>
          </label>
          <label className="test-preview-panel-row" htmlFor="preview-fast">
            <span>Acelerar el reloj de la pregunta ×20</span>
            <input id="preview-fast" type="checkbox" checked={fast} onChange={(e) => onFastChange(e.target.checked)} />
          </label>
          <div className="test-preview-panel-actions">
            <button type="button" onClick={onFill} disabled={busy}>Responder todo al azar</button>
            <button type="button" onClick={onExit}>Volver al panel</button>
          </div>
        </aside>
      )}
      <button type="button" className="test-preview-panel-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Ocultar controles' : 'Vista previa · controles'}
      </button>
    </div>
  );
};

const iconsTerms = [
  { icon: termIcon01, text: 'Procura realizar el test en un espacio sin distracciones.' },
  { icon: termIcon02, text: 'Confía en tu primera impresión al responder.' },
  { icon: termIcon03, text: 'Responde de forma honesta y espontánea.' },
];

const TestTerms = () => (
  <>
    <div className="gm-instructions">
      <h2>TE DAMOS LA BIENVENIDA A GROWME</h2>

      <p>Este test te permitirá conocer mejor tu forma de interactuar, tomar decisiones y desenvolverte en el trabajo.</p>
      <p>A partir de tus respuestas, obtendrás una mirada sobre tu estilo de comportamiento y sobre algunas tendencias en tu manera de actuar en distintos entornos profesionales.</p>
      <div className="test-duration">
        <div className="test-duration-head">
          <ClockIcon />
          <div>
            <span className="test-duration-label">Duración aproximada</span>
            <strong>{TEST_DURATION_TEXT}</strong>
            <span className="test-duration-typical">{TEST_DURATION_TYPICAL}</span>
          </div>
        </div>
        <ul className="test-duration-parts">
          <li><span>1. Autopercepción</span><span><b>Unos 10 min</b> · 18 preguntas · sin tiempo límite</span></li>
          <li><span>2. Situaciones laborales</span><span><b>Unos 25 min</b> · {EXPECTED_COMPETENCY_QUESTIONS} preguntas · hasta {QUESTION_MINUTES} min cada una · <strong>sin pausas</strong></span></li>
          <li><span>Cierre</span><span><b>Unos 10 min</b> · 2 respuestas breves por escrito · sin tiempo límite</span></li>
        </ul>
        <p className="test-duration-tip">Te recomendamos hacerlo de una vez, en un momento en que tengas ese tiempo disponible y sin interrupciones.</p>
      </div>
      <p><strong>¿Cómo funciona?</strong></p>
      <p>El test está compuesto por <strong>dos secciones:</strong></p>
      <p><strong>1. Autopercepción</strong></p>
      <p>En esta parte encontrarás una serie de afirmaciones sobre formas de actuar y relacionarse.</p>
      <p>Selecciona aquellas que mejor te representen en tu día a día laboral.</p>
      <p><strong>2. Situaciones Laborales</strong></p>
      <p>Se presentarán distintos escenarios del mundo del trabajo.</p>
      <p>Tu tarea será indicar cómo actuarías frente a cada una.</p>
      <p><strong>Antes de comenzar:</strong></p>
      <p>&#10004; No existen respuestas correctas ni incorrectas.</p>
      <p>&#10004; Responde de manera honesta y espontánea.</p>
      <p>&#10004; Elige las opciones que mejor reflejen tu comportamiento habitual.</p>
    </div>
    <p className="gm-is-ready-p"><strong>¿Listo(a) para comenzar?</strong></p>
    <div className="icons-terms">
      {iconsTerms.map((item, index) => (
        <div key={index} className="icon-term">
          <img src={item.icon} alt={`icon-term-${index}`} />
          <p>{item.text}</p>
        </div>
      ))}
    </div>
  </>
);

// La prueba de conexión no pasó por ninguna vía: se avisa antes de empezar, en vez
// de dejar que la persona responda 40 minutos que no van a llegar.
const ConnectionBlocked = ({ verifying, onRetry, onContinue }) => (
  <div className="connection-blocked">
    <h2>Tu conexión está bloqueando la plataforma</h2>
    <p>
      Hicimos una prueba y tu red no deja que tus respuestas lleguen a nuestra plataforma.
      Esto suele pasar en redes de empresa, que bloquean algunos sitios.
    </p>
    <p><strong>Antes de empezar, te recomendamos:</strong></p>
    <ul>
      <li>Responder desde tu celular usando datos móviles (no el wifi de la empresa).</li>
      <li>O conectarte a otra red, como la de tu casa.</li>
      <li>Si usas VPN, desactivarla.</li>
    </ul>
    <div className="connection-blocked-actions">
      <button type="button" onClick={onRetry} disabled={verifying}>
        {verifying ? 'Verificando tu conexión...' : 'Ya cambié de red, verificar de nuevo'}
      </button>
      <button type="button" className="secondary" onClick={onContinue} disabled={verifying}>
        Continuar de todas formas
      </button>
    </div>
    <p className="connection-blocked-note">
      Si continúas, tus respuestas se irán guardando en este equipo y se enviarán cuando la
      conexión lo permita. No cambies de navegador ni de computador hasta terminar.
    </p>
  </div>
);

// `key` en el título y las opciones (no en el timer, que debe conservar su estado)
// para que cada pregunta nueva entre con la transición suave.
const Question = ({ question, onChangeOption, resetTimer, initialSeconds, tickMs }) => (
  <>
    <h2 key={`title-${question.id}`} className="test-fade-in">{question.spanish}</h2>
    <QuestionTimer resetTimer={resetTimer} initialSeconds={initialSeconds} tickMs={tickMs} />
    <div key={`options-${question.id}`} className="test-fade-in">
      {question.options.map((option, index) => (
        <div className="question-option" key={index} onClick={onChangeOption(index)}>
          <input
            type="radio"
            id={`option-${option.id}`}
            name={`question-${question.id}`}
            value={option.id}
            onChange={onChangeOption(index)}
            checked={option.selected || false}
          />
          <label htmlFor={`option-${option.id}`}>{option.spanish}</label>
        </div>
      ))}
    </div>
  </>
);

const formatDuration = (totalSeconds) => {
  const pad = (n) => String(n).padStart(2, '0');
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
};

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 2.5M9 2h6" />
  </svg>
);

// Tiempo que lleva la persona en el test, desde que aceptó los términos. Reemplaza la
// cuenta regresiva global de 60 min. Es solo informativo: no controla nada.
const ElapsedTime = ({ startedAt, stoppedAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (stoppedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stoppedAt]);

  const start = startedAt ? new Date(startedAt).getTime() : NaN;
  if (Number.isNaN(start)) return null;
  const seconds = Math.max(0, Math.floor(((stoppedAt || now) - start) / 1000));
  return (
    <div className="test-elapsed" role="timer" aria-live="off">
      <ClockIcon />
      <span className="test-elapsed-label">{stoppedAt ? 'Tiempo total' : 'Tiempo en el test'}</span>
      <span className="test-elapsed-value">{formatDuration(seconds)}</span>
    </div>
  );
};

// Barra de progreso del encabezado, en tres tramos: Autopercepción, Situaciones
// laborales y Cierre. Se calcula desde el estado del test; no guarda nada propio.
const TestProgress = ({ step, discDone, discTotal, competencyIndex, competencyTotal, fundamentalIndex }) => {
  const afterDisc = ![STEPS.TERMS, STEPS.DISC_PHASE].includes(step);
  const afterCompetency = [STEPS.INTRO_ANSWER_FUNDAMENTALS, STEPS.ANSWER_FUNDAMENTALS, STEPS.FINISHED].includes(step);

  const discUnits = step === STEPS.TERMS ? 0 : afterDisc ? discTotal : discDone;
  const competencyUnits = afterCompetency ? competencyTotal : step === STEPS.COMPETENCY_PHASE ? competencyIndex : 0;
  const fundamentalUnits = step === STEPS.FINISHED ? MAX_ANSWER_FUNDAMENTALS : step === STEPS.ANSWER_FUNDAMENTALS ? fundamentalIndex : 0;
  const total = discTotal + competencyTotal + MAX_ANSWER_FUNDAMENTALS;
  const percent = Math.round(((discUnits + competencyUnits + fundamentalUnits) / total) * 100);

  const activeSegment = {
    [STEPS.DISC_PHASE]: 0,
    [STEPS.PRE_COMPETENCY]: 1,
    [STEPS.COMPETENCY_PHASE]: 1,
    [STEPS.INTRO_ANSWER_FUNDAMENTALS]: 2,
    [STEPS.ANSWER_FUNDAMENTALS]: 2,
  }[step];
  const segments = [
    { name: '1 · Autopercepción', weight: 18, fill: discUnits / discTotal },
    { name: '2 · Situaciones laborales', weight: 24, fill: competencyUnits / competencyTotal },
    { name: 'Cierre', weight: 7, fill: fundamentalUnits / MAX_ANSWER_FUNDAMENTALS },
  ];

  const where = {
    [STEPS.TERMS]: 'Bienvenida',
    [STEPS.DISC_PHASE]: `Parte 1 de 2 · ${discDone} de ${discTotal} preguntas ordenadas`,
    [STEPS.PRE_COMPETENCY]: 'Parte 1 terminada · Antes de la parte 2',
    [STEPS.COMPETENCY_PHASE]: `Parte 2 de 2 · Pregunta ${competencyIndex + 1} de ${competencyTotal}`,
    [STEPS.INTRO_ANSWER_FUNDAMENTALS]: 'Cierre · Profundizando en tus decisiones',
    [STEPS.ANSWER_FUNDAMENTALS]: `Cierre · Respuesta ${fundamentalIndex + 1} de ${MAX_ANSWER_FUNDAMENTALS}`,
    [STEPS.FINISHED]: 'Test finalizado',
  }[step];

  return (
    <div className="test-progress" role="group" aria-label="Avance del test">
      <div className="test-progress-meta">
        <span className="test-progress-where">{where}</span>
        <span className="test-progress-percent">{step === STEPS.FINISHED ? 100 : percent}% completado</span>
      </div>
      <div className="test-progress-segments">
        {segments.map((segment, index) => {
          const isActive = activeSegment === index;
          const isDone = segment.fill >= 1 && !isActive;
          return (
            <div
              key={segment.name}
              className={`test-progress-segment${isActive ? ' active' : ''}${isDone ? ' done' : ''}`}
              style={{ flex: segment.weight }}
            >
              <div className="test-progress-bar">
                <div className="test-progress-fill" style={{ width: `${Math.round(Math.min(1, segment.fill) * 100)}%` }} />
              </div>
              <span className="test-progress-name">{segment.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Aviso antes de la parte con tiempo por pregunta. Hasta que la persona confirma,
// el tiempo no corre.
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12 3 3 5-6" />
  </svg>
);

const PreCompetency = ({ questionCount, ready, onReadyChange }) => (
  <div className="pre-competency">
    <p className="test-cheer">Primera parte lista. ¡Buen trabajo!</p>
    <h2>Segunda parte: Situaciones laborales</h2>
    <p className="pre-competency-lead">
      Esta parte tiene <strong>tiempo por pregunta</strong>. Cuando empieces, el tiempo corre y <strong>no se puede pausar</strong>.
    </p>
    <div className="pre-competency-facts">
      <div><strong>{questionCount}</strong><span>situaciones laborales</span></div>
      <div><strong>{QUESTION_MINUTES} min</strong><span>por pregunta</span></div>
      <div className="pre-competency-fact-strong"><strong>Sin pausas</strong><span>una vez que empiezas</span></div>
    </div>
    <div className="pre-competency-prep">
      <p>Antes de comenzar, asegúrate de:</p>
      <ul>
        <li><CheckIcon /><span>Haber atendido cualquier necesidad personal.</span></li>
        <li><CheckIcon /><span>Encontrarte en un espacio tranquilo y avisar a tu entorno que no debes ser interrumpido(a).</span></li>
        <li><CheckIcon /><span>Silenciar las notificaciones de tu celular y cerrar otras pestañas, correos y chats.</span></li>
        <li><CheckIcon /><span>Contar con una conexión a internet estable y, si usas notebook o celular, con batería suficiente.</span></li>
      </ul>
    </div>
    <div className="pre-competency-warning">
      Si cierras o sales de la página, el tiempo de la pregunta sigue corriendo. Al volver retomarás
      donde quedaste, pero con el tiempo que te quede.
    </div>
    <label className="pre-competency-confirm" htmlFor="ready-for-timed-part">
      <input
        type="checkbox"
        id="ready-for-timed-part"
        checked={ready}
        onChange={(e) => onReadyChange(e.target.checked)}
      />
      <span>Entiendo que esta parte no se puede pausar y estoy listo(a) para comenzar.</span>
    </label>
  </div>
);

// Reloj por pregunta: se siembra con initialSeconds sólo en el primer montaje (útil al
// hidratar tras recarga). En cada cambio de pregunta (resetTimer) reinicia al valor completo.
// Al agotarse muestra un aviso en la página, solo si se agotó mientras se respondía:
// al retomar un test interrumpido el tiempo ya venía vencido y no debe saltar al cargar.
// `tickMs` solo cambia en la vista previa (reloj acelerado); en el test es 1 s.
const QuestionTimer = ({ resetTimer, initialSeconds, tickMs = 1000 }) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds != null ? initialSeconds : QUESTION_TIME);
  // Si la pregunta arrancó con tiempo, llegar a 0 significa que se agotó en esta sesión.
  const startedWithTimeRef = useRef(timeLeft > 0);
  const expiredWhileAnswering = timeLeft === 0 && startedWithTimeRef.current;
  const prevResetRef = useRef(resetTimer);

  useEffect(() => {
    // Sólo reinicia al valor completo cuando cambia realmente de pregunta; en el
    // montaje/hidratación conserva el valor sembrado por el servidor. Comparar el
    // valor previo (en vez de un flag "ya corrí") lo hace inmune al doble efecto de
    // React.StrictMode en desarrollo.
    if (prevResetRef.current !== resetTimer) {
      prevResetRef.current = resetTimer;
      startedWithTimeRef.current = true;
      setTimeLeft(QUESTION_TIME);
    }
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(id);
          return 0;
        }
        return t - 1;
      });
    }, tickMs);
    return () => clearInterval(id);
  }, [resetTimer, tickMs]);

  // Anillo que se vacía con el tiempo; en los últimos 30 s pasa a ámbar (no rojo).
  const remainingRatio = Math.max(0, Math.min(1, timeLeft / QUESTION_TIME));
  return (
    <>
      <div className={`question-timer${timeLeft <= 30 ? ' low' : ''}`}>
        <svg className="question-timer-ring" viewBox="0 0 120 120" aria-hidden="true">
          <circle className="question-timer-track" cx="60" cy="60" r="52" />
          <circle
            className="question-timer-fill"
            cx="60"
            cy="60"
            r="52"
            pathLength="100"
            strokeDasharray="100"
            strokeDashoffset={100 - remainingRatio * 100}
          />
        </svg>
        <div className="question-timer-text">
          <span>{formatDuration(timeLeft)}</span>
          <small>{timeLeft < 60 ? 'segundos' : 'minutos'}</small>
        </div>
      </div>
      {expiredWhileAnswering && (
        <p className="time-notice" role="status">
          Se cumplió el tiempo sugerido para esta pregunta. No sobrepienses: elige la opción que más te represente.
        </p>
      )}
    </>
  );
};

const IntroAnswerFundamentals = ({ questionCount }) => (
  <div className="intro-answer-fundamentals">
    <p className="test-cheer">Ya respondiste las {questionCount} situaciones. Solo faltan 2 respuestas breves.</p>
    <h2>Profundizando en sus decisiones</h2>
    <p>En esta sección buscamos comprender con mayor profundidad las razones que hay detrás de algunas de sus elecciones realizadas durante el test.</p>
    <p>Esta parte del test <strong>no tiene límite de tiempo por pregunta.</strong></p>
    <p>Se han seleccionado al azar algunas de sus respuestas anteriores. En cada caso, deberá explicar brevemente por qué eligió actuar de esa manera.</p>
    <p>Por ejemplo: <i>"Elegí esta alternativa porque considero importante que mis ideas sean tomadas en cuenta y que mi opinión sea percibida de manera positiva."</i></p>
    <p>Recuerde que <strong>no existen respuestas correctas o incorrectas</strong>. El objetivo de esta evaluación es comprender mejor su forma de actuar en el entorno laboral y ofrecer información que apoye su desarrollo profesional.</p>
  </div>
);

const AnswerFundamentals = ({ question, onChangeFundamentalInput }) => {
  const option = question.options.find(opt => opt.selected);
  const length = (question.answerFundamental || '').length;
  const missing = MIN_FUNDAMENTAL_LENGTH - length;
  return (
    <div className="answer-fundamentals">
      <h2>{question.spanish}</h2>
      {/* Sin opción marcada (snapshot dañado) se omite el recuadro en vez de romper
          la página, que dejaba al candidato con la pantalla en blanco. */}
      {option && (
        <div className="question-option">
          <input type="radio" id={`option-${option.id}`} name={`question-${question.id}`} value={option.id} checked disabled />
          <label htmlFor={`option-${option.id}`}>{option.spanish}</label>
        </div>
      )}
      <p>Fundamenta tu respuesta (al menos {MIN_FUNDAMENTAL_LENGTH} caracteres)</p>
      <textarea
        className="fundamental-text"
        name="fundamental-text"
        id="fundamental-text"
        value={question.answerFundamental || ''}
        onChange={onChangeFundamentalInput}
      />
      {/* Un solo texto, no dos: con el traductor del navegador activo, React fallaba
          (removeChild) al quitar "Te faltan…" justo al llegar al mínimo, y la página
          se caía en cada intento. */}
      <p className="character-counter">
        {`${missing > 0 ? `Te faltan ${missing} caracteres · ` : ''}${length}/700`}
      </p>
    </div>
  );
};

const Finished = () => (
  <div className="finished-test">
    <h2>Test finalizado</h2>
    <img src={emailIcon} alt="icon" />
    <p><strong>Growme</strong> ha sido completado correctamente.</p>
    <p>Tus resultados serán enviados a tu correo electrónico en breve.</p>
    <p className="test-cheer">Gracias por tu tiempo y por responder con sinceridad.</p>
  </div>
);

export default UnifiedTest;
